import {Button, Spin} from 'antd';
import React, {FC} from 'react';
import {useHistory} from 'react-router-dom';

import {ListArrayFactory} from 'components/@common/specific/list-array-factory';
import {SignatureActionButton} from 'components/ais/reclaiming-information-card/fields/signature-action-button';
import {SignatureItem} from 'components/ais/reclaiming-information-card/fields/signature-item/signature-item';
import {useUploadSignature} from 'components/ais/reclaiming-information-card/hooks';
import {DateListItem} from 'components/form/fields/date-list-item';
import {DownloadDocumentListItem} from 'components/form/fields/download-document-list-item';
import {URL_AIS3_RESPONSE_REQUIREMENT_EXPLANATIONS} from 'shared/constants/urls';
import {useEntityFromLocation} from 'shared/hooks/use-entity-from-location';
import {useUserRoleFunctions} from 'shared/hooks/use-user-role-functions';
import {
    TExplanationInformationEntity,
    useLazyGetExplanationInformationQuery,
    useRunSubmitToDocRequestServiceMutation,
} from 'store/api/explanation-information/explanation-information.api';
import {EUserRoleFunction} from 'store/slices/user-slice/user-slice-role-functions';

interface IExplanationInformationMainInfoProps {
    id: string;
    initialLoadedData: TExplanationInformationEntity;
    onClose: () => void;
}

export const ExplanationInformationMainInfo: FC<IExplanationInformationMainInfoProps> = ({
    id,
    initialLoadedData,
    onClose,
}: IExplanationInformationMainInfoProps) => {
    const history = useHistory();
    const {entityName: entityNameFromLocation} = useEntityFromLocation();

    const entityName = entityNameFromLocation ?? 'fileUpload';

    const [
        runSubmitExplanationProtocolService,
        {isLoading: isSubmitProtocolLoading},
    ] = useRunSubmitToDocRequestServiceMutation();

    const [
        runSubmitExplanationResponseService,
        {isLoading: isSubmitResponseToDocRequestLoading},
    ] = useRunSubmitToDocRequestServiceMutation();

    const [
        getExplanationInformationQuery,
        {
            currentData,
            isFetching: isDataRefetching,
        },
    ] = useLazyGetExplanationInformationQuery();

    const reloadData = () => {
        getExplanationInformationQuery({id});
    };

    const explanationInfo = currentData ?? initialLoadedData;

    const [isProtocolSignatureUploading, handleUploadProtocolSignature] = useUploadSignature({
        attachmentId: explanationInfo?.protocolDocument?.attachmentId,
        reloadData,
    });
    const [isResponseToDocSignatureUploading, handleUploadResponseToDocSignature] = useUploadSignature({
        attachmentId: explanationInfo?.responseDocument?.attachmentId,
        reloadData,
    });

    const handleCreateInventoryClick = () => {
        const docIdParam = `?docId=${explanationInfo?.mainDocument?.objectId}`;
        history.push(`${URL_AIS3_RESPONSE_REQUIREMENT_EXPLANATIONS}${docIdParam}`);
    };

    const renderFormationButton = () => (
        <Button
            type="primary"
            onClick={handleCreateInventoryClick}
        >
            Сформировать
        </Button>
    );
    const {hasUserRoleFunction} = useUserRoleFunctions({sections: [entityName]});
    const isDownloadAllowed = hasUserRoleFunction(EUserRoleFunction.DOWNLOAD);

    return (
        <Spin
            spinning={isDataRefetching}
            tip="Подпись загружена, обновляем данные..."
        >
            <ListArrayFactory
                listArrayDescription={[
                    {
                        listTitle: 'Основная информация о требовании',
                        list: [
                            {
                                title: 'Номер требования:',
                                description: explanationInfo?.mainDocNumber || '-',
                            },
                            {
                                title: 'Дата получения:',
                                description: <DateListItem
                                    date={explanationInfo?.downloadDate}
                                />,
                            },
                            {
                                title: 'Файл требования:',
                                description: <DownloadDocumentListItem
                                    isDownloadDisabled={!isDownloadAllowed}
                                    fileList={
                                        explanationInfo?.mainDocument
                                            ? [explanationInfo?.mainDocument]
                                            : undefined
                                    }
                                    entityName={entityName}
                                />,
                            },
                            {
                                title: 'Подпись:',
                                description: <SignatureItem
                                    signatureName={explanationInfo?.mainDocument?.signatureFileName}
                                    attachmentId={explanationInfo?.mainDocument?.attachmentId}
                                    entityName={entityName}
                                    isDownloadDisabled={!isDownloadAllowed}
                                />,
                            },
                        ],
                    },
                    {
                        listTitle: 'Протокол обработки требования',
                        list: [{
                            title: 'Файл протокола:',
                            description: <DownloadDocumentListItem
                                isDownloadDisabled={!isDownloadAllowed}
                                fileList={
                                    explanationInfo?.protocolDocument
                                        ? [explanationInfo?.protocolDocument] : undefined
                                }
                                entityName={entityName}

                            />,
                        },
                        {
                            title: 'Подпись:',
                            description: <SignatureItem
                                signatureName={explanationInfo?.protocolDocument?.signatureFileName}
                                attachmentId={explanationInfo?.protocolDocument?.attachmentId}
                                entityName={entityName}
                                isDownloadDisabled={!isDownloadAllowed}
                            />,
                        },
                        {
                            title: 'Дата отправки:',
                            description: <DateListItem
                                date={explanationInfo?.processingAudit?.createdDate}
                            />,
                        },
                        ],
                        useButtonsBottomMargin: true,
                        buttons: [
                            <SignatureActionButton
                                signatureName={explanationInfo?.protocolDocument?.signatureFileName}
                                attachmentId={explanationInfo?.protocolDocument?.attachmentId}
                                attachmentFileName={explanationInfo?.protocolDocument?.fileName}
                                isSignatureUploading={isProtocolSignatureUploading}
                                handleUploadFile={handleUploadProtocolSignature}
                            />,
                            <Button
                                type="primary"
                                disabled={
                                    !explanationInfo?.protocolDocument?.attachmentId
                                        || !explanationInfo?.protocolDocument?.signatureFileName
                                }
                                onClick={() => {
                                    runSubmitExplanationProtocolService({
                                        externalServiceName: 'submitExplanationProtocol',
                                        mainDocNumber: explanationInfo?.mainDocNumber,
                                    })
                                        .unwrap()
                                        .then(onClose);
                                }}
                                loading={isSubmitProtocolLoading}
                            >
                                Отправить
                            </Button>,
                        ],
                    },
                    {
                        listTitle: 'Ответ на требование',
                        list: [{
                            title: 'Файл ответа на требование:',
                            description: <DownloadDocumentListItem
                                isDownloadDisabled={!isDownloadAllowed}
                                fileList={
                                    explanationInfo?.responseDocument
                                        ? [explanationInfo?.responseDocument] : undefined
                                }
                                entityName={entityName}
                            />,
                        },
                        {
                            title: 'Подпись:',
                            description: <SignatureItem
                                signatureName={explanationInfo?.responseDocument?.signatureFileName}
                                attachmentId={explanationInfo?.responseDocument?.attachmentId}
                                entityName={entityName}
                                isDownloadDisabled={!isDownloadAllowed}
                            />,
                        },
                        {
                            title: 'Дата отправки:',
                            description: <DateListItem
                                date={explanationInfo?.responseRequirementAudit?.createdDate}
                            />,
                        },
                        ],
                        useButtonsBottomMargin: true,
                        buttons: [
                            renderFormationButton(),
                            <SignatureActionButton
                                signatureName={explanationInfo?.responseDocument?.signatureFileName}
                                attachmentId={explanationInfo?.responseDocument?.attachmentId}
                                attachmentFileName={explanationInfo?.responseDocument?.fileName}
                                isSignatureUploading={isResponseToDocSignatureUploading}
                                handleUploadFile={handleUploadResponseToDocSignature}
                            />,
                            <Button
                                type="primary"
                                disabled={
                                    !explanationInfo?.responseDocument?.attachmentId
                                    || !explanationInfo?.responseDocument?.signatureFileName
                                }
                                onClick={() => {
                                    runSubmitExplanationResponseService({
                                        externalServiceName: 'submitExplanationResponse',
                                        mainDocNumber: explanationInfo?.mainDocNumber,
                                    })
                                        .unwrap()
                                        .then(onClose);
                                }}
                                loading={isSubmitResponseToDocRequestLoading}
                            >
                                Отправить
                            </Button>,
                        ],
                    },
                    {
                        listTitle: 'Протокол обработки ответа на требование',
                        list: [{
                            title: 'Файл протокола:',
                            description: <DownloadDocumentListItem
                                isDownloadDisabled={!isDownloadAllowed}
                                fileList={
                                    explanationInfo?.incomingProcessingAudit?.mainDocument
                                        ? [explanationInfo?.incomingProcessingAudit?.mainDocument] : undefined
                                }
                                entityName={entityName}
                            />,
                        },
                        {
                            title: 'Подпись:',
                            description: <SignatureItem
                                signatureName={
                                    explanationInfo?.incomingProcessingAudit?.mainDocument?.signatureFileName
                                }
                                attachmentId={explanationInfo?.incomingProcessingAudit?.mainDocument?.attachmentId}
                                entityName={entityName}
                                isDownloadDisabled={!isDownloadAllowed}
                            />,
                        },
                        {
                            title: 'Дата получения:',
                            description: <DateListItem
                                date={explanationInfo?.incomingProcessingAudit?.createdDate}
                            />,
                        },
                        ],
                        useButtonsBottomMargin: true,
                    },
                ]}
            />
        </Spin>
    );
};
